import {
  ShieldCheck,
  MonitorSmartphone,
  RefreshCcw,
  BrainCircuit,
  ExternalLink,
} from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const quals = [
  {
    icon: ShieldCheck,
    title: "Fachkraft für Datenschutz und -sicherheit (IHK)",
    fun: "Datenschutz, Informationssicherheit und sichere organisatorische Prozesse.",
    text: "Diese Qualifikation bildet eine wesentliche fachliche Grundlage für die Einordnung von Datenschutz- und Sicherheitsanforderungen.",
  },
  {
    icon: MonitorSmartphone,
    title: "Fachkraft für Digitalisierung (IHK)",
    fun: "Digitale Prozesse verstehen, gestalten und organisatorisch sinnvoll weiterentwickeln.",
    text: "Damit wird Datenschutz nicht isoliert betrachtet, sondern im Zusammenhang mit den digitalen Abläufen moderner Organisationen.",
  },
  {
    icon: RefreshCcw,
    title: "Fachkraft für Change Management (IHK)",
    fun: "Veränderungen in Organisationen strukturiert begleiten und Menschen in Veränderungsprozesse einbeziehen.",
    text: "Denn wirksamer Datenschutz besteht nicht nur aus Regeln. Neue Prozesse müssen von Menschen verstanden und im Alltag gelebt werden.",
  },
  {
    icon: BrainCircuit,
    title: "Manager für angewandte KI-Transformation (IHK)",
    fun: "Organisationen bei der strukturierten und verantwortungsvollen Einführung und Nutzung von KI unterstützen.",
    text: "Gerade mit zunehmender Digitalisierung und KI-Nutzung werden Datenschutz, Informationssicherheit, Prozesse und Veränderungsmanagement immer stärker miteinander verbunden.",
  },
];

const Author = () => (
  <section
    id="autor"
    data-testid="author-section"
    className="py-24 sm:py-32 bg-navy-900 border-y border-white/5"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead overline="Der Autor" title="Über den Autor" />

      <div className="mt-14 grid gap-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-start">
        <Reveal>
          <figure className="relative max-w-sm">
            <div
              aria-hidden="true"
              className="absolute -inset-4 rounded-2xl bg-[radial-gradient(circle,rgba(0,102,255,0.2)_0%,rgba(13,20,36,0)_75%)]"
            />
            <img
              src="/assets/autor.jpg"
              alt="Portrait von Jörg Eisbach, Autor von BlaulichtPraxis"
              data-testid="author-photo"
              className="relative w-full rounded-xl border border-white/10 object-cover"
              loading="lazy"
            />
            <figcaption className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-gray-500">
              Jörg Eisbach · Autor
            </figcaption>
          </figure>
        </Reveal>

        <Reveal delay={0.1}>
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
            Jörg Eisbach
          </h3>
          <p className="mt-5 text-base md:text-lg text-gray-300 leading-relaxed">
            Jörg Eisbach verbindet Datenschutzwissen mit Digitalisierung,
            Veränderungsmanagement und der praktischen Einführung neuer
            Technologien in Organisationen.
          </p>
          <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed">
            Er ist als{" "}
            <strong className="text-gray-200">
              bestellter Datenschützer der Stadt Lahnstein für die Freiwillige
              Feuerwehr Lahnstein tätig
            </strong>{" "}
            und kennt dadurch Datenschutzfragen nicht nur aus theoretischer
            Sicht, sondern auch aus dem konkreten organisatorischen Umfeld einer
            Freiwilligen Feuerwehr.
          </p>
          <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed">
            Sein Ansatz ist darauf ausgerichtet, rechtliche, organisatorische
            und technologische Anforderungen verständlich in die Praxis zu
            übersetzen.
          </p>
          <a
            href="https://www.joerg-eisbach.de/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="author-homepage-link"
            className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-blau-glow transition-colors duration-200 hover:text-white"
          >
            Mehr über Jörg Eisbach
            <ExternalLink size={14} aria-hidden="true" />
          </a>
        </Reveal>
      </div>

      <div className="mt-16 grid gap-5 sm:grid-cols-2">
        {quals.map((q, i) => (
          <Reveal key={q.title} delay={(i % 2) * 0.1} className="h-full">
            <article
              data-testid={`author-qual-${i + 1}`}
              className="card-glow h-full rounded-xl border border-white/8 bg-navy-800/70 p-7 transition-[border-color,transform] duration-300 hover:border-blau/40 hover:-translate-y-1"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-blau/30 bg-blau/10 text-blau-glow">
                <q.icon size={20} strokeWidth={1.8} aria-hidden="true" />
              </div>
              <h4 className="mt-5 font-display text-base sm:text-lg font-bold text-white leading-snug">
                {q.title}
              </h4>
              <p className="mt-3 text-sm font-medium text-gray-300">
                <span className="text-blau-glow">Funktion:</span> {q.fun}
              </p>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                {q.text}
              </p>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <blockquote
          data-testid="author-holistic-quote"
          className="mt-14 rounded-xl border border-blau/25 bg-blau/8 p-8 sm:p-10"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blau-glow">
            Ein ganzheitlicher Blick auf Organisationen
          </p>
          <p className="mt-4 text-base md:text-lg text-gray-200 leading-relaxed">
            „Die Verbindung aus Datenschutz und -sicherheit, Digitalisierung,
            Change Management und angewandter KI-Transformation ermöglicht einen
            ganzheitlichen Blick auf moderne Organisationen: von rechtlichen und
            technischen Anforderungen über Prozesse bis zur erfolgreichen
            Umsetzung durch die Menschen, die täglich damit arbeiten.“
          </p>
        </blockquote>
      </Reveal>
    </div>
  </section>
);

export default Author;
